/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import type { LearnerProfile_WorkHistory as WorkHistory } from '__generated__/graphql-types-do-not-use';

import { Button, Grid, InlineNotification, Typography, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import EditWorkHistoryModal from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-history/EditWorkHistoryModal';
import WorkHistoryBody from 'bundles/account-profile/components/recruiting-profile-viewer/components/work-history/WorkHistoryBody';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const styles = {
  sectionTitle: () => css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
};

const E2E_SELECTORS = {
  WORK_SECTION: 'profile-work-section',
  WORK_SECTION_ADD: 'profile-work-section-add',
} as const;

const WorkHistorySection = () => {
  const [isWorkHistoryModalOpen, setIsWorkHistoryModalOpen] = useState<boolean>(false);
  const [selectedWorkHistoryItem, setSelectedWorkHistoryItem] = useState<WorkHistory | undefined>(undefined);
  const [shouldRefreshModalDataFlag, setShouldRefreshModalDataFlag] = useState<boolean>(false);
  const { isProfileLoading, profileState = {}, editable } = useContext(ProfileContext);
  const { workHistory = [] } = profileState;

  if (!isProfileLoading && workHistory.length === 0 && !editable) return null;

  const handleOpenWorkHistoryModal = (workHistoryItem?: WorkHistory) => {
    setIsWorkHistoryModalOpen(true);
    if (workHistoryItem) setSelectedWorkHistoryItem(workHistoryItem);
  };

  const handleCloseWorkHistoryModal = () => {
    setIsWorkHistoryModalOpen(false);
    setSelectedWorkHistoryItem(undefined);
    setShouldRefreshModalDataFlag((prevState) => !prevState);
  };

  return (
    <ProfileViewerCard data-e2e={E2E_SELECTORS.WORK_SECTION}>
      <EditWorkHistoryModal
        isOpen={isWorkHistoryModalOpen}
        onClose={handleCloseWorkHistoryModal}
        selectedWorkHistoryItem={selectedWorkHistoryItem}
        refreshDataFlag={shouldRefreshModalDataFlag}
      />
      <Grid container direction="row" justifyContent="space-between" alignItems="center" css={styles.sectionTitle}>
        <Typography variant="h2semibold" component="h3" data-testid="work-history-section-title">
          {_t('Work history')}
        </Typography>
        {editable && workHistory.length > 0 && (
          <div>
            <Button
              variant="secondary"
              iconPosition="before"
              icon={<AddIcon />}
              size="small"
              data-e2e={E2E_SELECTORS.WORK_SECTION_ADD}
              onClick={() => handleOpenWorkHistoryModal()}
              component={TrackedButton}
              // not tracked in eventing v3
              trackingName="profile_add_work_history"
              requireFullyVisible={false}
              withVisibilityTracking={true}
            >
              {_t('Add')}
            </Button>
          </div>
        )}
      </Grid>
      <WorkHistoryBody handleOpenWorkHistoryModal={handleOpenWorkHistoryModal} />
    </ProfileViewerCard>
  );
};

const ErrorBoundWorkHistorySection = () => (
  <ErrorBoundaryWithTagging
    componentName="WorkHistorySection"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the work history section of the page. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <WorkHistorySection />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundWorkHistorySection;
